/**
 * Model aliasing + mid-conversation switch resolution — pure; aliases come from ai.libx.js's catalog.
 *
 * Harness-level so every agentx consumer (unclaw, para-chat, …) resolves `[opus]`-style
 * aliases, gates by an allow-list, and announces a switch the same way. Policy (which models
 * a caller may use) and session state (the prior model) stay with the consumer; this module
 * is the shared mechanism.
 */
import { claudeModelAliases } from 'ai.libx.js';

/** Canonical short aliases → concrete Anthropic model ids — DERIVED from ai.libx.js's catalog (newest entry per
 *  family, plus versioned `opus-5`-style aliases). Never hand-pin an id here: a new model = one catalog entry. */
export const MODEL_ALIASES: Record<string, string> = { ...claudeModelAliases };

/** Newest catalog model of a Claude family as a provider-qualified id (`'sonnet'` → `'anthropic/claude-sonnet-5-5'`).
 *  Use this for every default/tier — a literal dated id goes stale the day the next model ships. */
export function latestClaude(family: 'fable' | 'opus' | 'sonnet' | 'haiku', provider = 'anthropic'): string {
  const id = MODEL_ALIASES[family];
  if (!id) throw new Error(`ai.libx.js catalog has no Claude ${family} model`);
  return `${provider}/${id}`;
}

/** Resolve an alias to a concrete id. A provider prefix ("anthropic/") is preserved; unknown ids pass through. */
export function resolveModelAlias(input: string): string {
  const slash = input.indexOf('/');
  const prefix = slash === -1 ? '' : input.slice(0, slash + 1);
  const rest = slash === -1 ? input : input.slice(slash + 1);
  // claude-code's catalogue IS the aliases (claude-code/opus); expanding them yields an unknown id.
  if (prefix === 'claude-code/') return input;
  return prefix + (MODEL_ALIASES[rest.toLowerCase()] ?? rest);
}

/** The provider of a model id ("cursor/x" -> "cursor"). Provider-specific `providerOptions` are only
 *  portable within the same provider — leaking them across is a hard 400.
 *
 *  A BARE id is the common case, not the exception: `resolveModelAlias('haiku')` yields
 *  `claude-haiku-5-5` with no prefix (ai.libx.js resolves it fuzzily downstream), so reading
 *  only the literal "/" made every aliased child look like a provider switch and silently dropped the
 *  parent's options. The fix belongs HERE — the alias table must keep emitting the ids ai.libx.js
 *  expects, and the comparison is provider identity, not string identity.
 *
 *  A bare id we can't attribute is UNKNOWN, not a shared provider: it gets a per-id marker so two
 *  different unknown ids never compare equal (returning "" for both made them look like one provider
 *  and would have leaked one host's options onto the other's model). An unknown id still compares
 *  equal to ITSELF, so a child staying on the parent's exact model keeps the parent's options. */
export function modelProvider(model: string): string {
  const i = model.indexOf('/');
  if (i !== -1) return model.slice(0, i);
  if (/^claude-/i.test(model)) return 'anthropic';
  return `?${model}`; // unknown: identity-scoped, never equal to a different unknown id
}

/** Short, human-friendly name for a resolved model id (e.g. "anthropic/claude-opus-4-8" → "opus"). */
export function modelShortLabel(model?: string): string {
  if (!model) return 'default';
  const id = model.split('/').pop()!;
  return id.replace(/^claude-/, '').replace(/-\d.*$/, '');
}

export interface ResolveModelSwitchOpts {
  /** Model requested for this turn (e.g. from an inline directive). Undefined = keep `current`. */
  requested?: string;
  /** The turn's default/current model when nothing is requested. */
  current: string;
  /** The session's prior resolved model — drives change detection + the notice. */
  prior?: string;
  /** If set, `requested` must match one of these (by short label) or it's denied and `current` is kept. */
  allowed?: string[];
  /** Announcement formatter. Default: `_[switching from x to y]_\n\n`. */
  format?: (from: string, to: string) => string;
}

export interface ModelSwitch {
  /** The model to run this turn. */
  model: string;
  /** True when `requested` was rejected by `allowed` and `model` fell back to `current`. */
  denied: boolean;
  /** Inline notice to surface when the model actually changed vs `prior`, else undefined. */
  notice?: string;
}

/**
 * Resolve which model a turn runs, given an optional request, an allow-list, and the prior model.
 * Comparisons are by short label so provider-prefixed and dated ids unify
 * ("anthropic/claude-opus-4-8" ≡ "claude-opus-4-8" ≡ "opus").
 */
export function resolveModelSwitch(opts: ResolveModelSwitchOpts): ModelSwitch {
  const { requested, current, prior, allowed } = opts;
  if (!requested) return { model: current, denied: false };
  if (allowed && !allowed.some((a) => modelShortLabel(a) === modelShortLabel(requested))) {
    return { model: current, denied: true };
  }
  const fmt = opts.format ?? ((from: string, to: string) => `_[switching from ${from} to ${to}]_\n\n`);
  const changed = prior !== undefined && modelShortLabel(prior) !== modelShortLabel(requested);
  return {
    model: requested,
    denied: false,
    notice: changed ? fmt(modelShortLabel(prior!), modelShortLabel(requested)) : undefined,
  };
}
